import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Loader2, AlertTriangle } from "lucide-react";

/**
 * Handles the /auth/magic?token=... route.
 * Mirrors AuthCallback: single-fire POST to /auth/magic/verify, setUser on
 * success, then navigate to the intended return URL (or /dashboard).
 */
export default function MagicCallback() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [state, setState] = useState("verifying"); // verifying | error
  const [message, setMessage] = useState("");
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const token = params.get("token");
    (async () => {
      if (!token) {
        setState("error");
        setMessage("This link is missing its token. Try requesting a new one.");
        return;
      }
      try {
        const { data } = await api.post("/auth/magic/verify", { token });
        setUser(data.user);
        let target = "/dashboard";
        try {
          const stored = sessionStorage.getItem("post_login_return");
          if (stored && stored.startsWith("/") && !stored.startsWith("/auth/")) {
            target = stored;
          }
          sessionStorage.removeItem("post_login_return");
        } catch (e) { console.debug("post_login_return read failed", e); }
        navigate(target, { replace: true, state: { user: data.user } });
      } catch (e) {
        const detail = e?.response?.data?.detail || "This link is invalid or has expired.";
        setState("error");
        setMessage(detail);
      }
    })();
  }, [params, navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-canvas">
      <div className="w-full max-w-md bevel border-hair rounded-sm p-8">
        {state === "verifying" ? (
          <div className="flex flex-col items-center text-center space-y-4" data-testid="magic-verify-loading">
            <Loader2 size={28} strokeWidth={1.5} className="animate-spin text-app" />
            <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted">AUTH / VERIFYING</div>
            <div className="font-display text-xl text-app">Signing you in&hellip;</div>
          </div>
        ) : (
          <div className="space-y-4" data-testid="magic-verify-error">
            <div className="flex items-center gap-2 text-[#F59E0B]">
              <AlertTriangle size={16} strokeWidth={2} />
              <span className="font-mono text-[10px] tracking-[0.2em] uppercase">LINK ERROR</span>
            </div>
            <div className="font-display text-xl text-app">We couldn&rsquo;t sign you in.</div>
            <p className="text-sm text-muted font-light leading-relaxed">{message}</p>
            <button
              type="button"
              onClick={() => navigate("/", { replace: true })}
              data-testid="magic-verify-back-to-login"
              className="mt-2 inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.2em] uppercase px-3 py-2 border border-[#0A0A0A] hover:bg-app hover:text-white transition-colors"
            >
              &larr; Back to sign in
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
